/**
 * Enhanced Hero Carousel Script
 * Provides auto-playing carousel with advanced features and responsive design
 * Fixed: Proper null checks and type safety
 * @class EnhancedHeroCarousel
 */

class EnhancedHeroCarousel {
  /**
   * @param {string} sectionId - The section ID
   */
  constructor(sectionId) {
    if (!sectionId || typeof sectionId !== 'string') return;
    
    this.heroCarousel = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (!this.heroCarousel) return;

    this.slidesContainer = this.heroCarousel.querySelector('[data-slides-container]');
    this.slides = Array.from(this.heroCarousel.querySelectorAll('[data-slide-index]') || []);
    this.prevBtn = this.heroCarousel.querySelector('[data-carousel-prev]');
    this.nextBtn = this.heroCarousel.querySelector('[data-carousel-next]');
    this.dotsContainer = this.heroCarousel.querySelector('[data-carousel-dots]');
    this.dots = Array.from(this.dotsContainer?.querySelectorAll('[data-dot-index]') || []);

    this.currentIndex = 0;
    this.autoPlayInterval = null;
    this.isAutoPlaying = true;
    this.touchStartX = 0;
    this.touchEndX = 0;

    // Validate slides exist
    if (!this.slides || this.slides.length === 0) {
      console.warn('EnhancedHeroCarousel: No slides found');
      return;
    }

    // Get settings from element styles
    this.autoPlaySpeed = this.parseStyleVariable('--carousel-auto-play-speed') || 5000;
    this.transitionDuration = this.parseStyleVariable('--carousel-transition-duration') || 600;
    this.autoPlay = this.getAutoPlaySetting();

    this.init();
  }

  init() {
    if (!this.heroCarousel || !this.slides || this.slides.length === 0) return;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.handlePrevClick());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.handleNextClick());
    }

    if (this.dots && this.dots.length > 0) {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', (e) => this.handleDotClick(e));
      });
    }

    // Mouse events
    if (this.heroCarousel) {
      this.heroCarousel.addEventListener('mouseenter', () => this.pauseAutoPlay());
      this.heroCarousel.addEventListener('mouseleave', () => this.resumeAutoPlay());

      // Touch events for mobile
      // @ts-ignore
      this.heroCarousel.addEventListener('touchstart', (e) => this.handleTouchStart(e), false);
      // @ts-ignore
      this.heroCarousel.addEventListener('touchend', (e) => this.handleTouchEnd(e), false);

      // Keyboard navigation
      // @ts-ignore
      this.heroCarousel.addEventListener('keydown', (e) => this.handleKeydown(e));

      // Accessibility improvements
      this.heroCarousel.setAttribute('role', 'region');
      this.heroCarousel.setAttribute('aria-label', 'Hero Carousel');
      this.heroCarousel.setAttribute('aria-live', 'polite');
    }

    // Start auto play
    this.startAutoPlay();
  }

  /**
   * @param {string} varName - The CSS variable name
   * @returns {number|null} The parsed value
   */
  parseStyleVariable(varName) {
    if (!this.heroCarousel || !varName) return null;
    try {
      const value = getComputedStyle(this.heroCarousel).getPropertyValue(varName).trim();
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? null : parsed;
    } catch (e) {
      console.warn('Error parsing style variable:', varName, e);
      return null;
    }
  }

  getAutoPlaySetting() {
    if (!this.heroCarousel) return true;
    const autoPlayText = this.heroCarousel.getAttribute('data-auto-play');
    return autoPlayText !== 'false';
  }

  /**
   * @param {number} index - The slide index to go to
   */
  goToSlide(index) {
    if (!this.slides || this.slides.length === 0 || typeof index !== 'number') return;

    this.currentIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;

    if (this.slidesContainer && this.slidesContainer instanceof HTMLElement) {
      this.slidesContainer.style.transform = `translateX(-${this.currentIndex * 100}%)`;
    }

    // Update dots
    if (this.dots && this.dots.length > 0) {
      this.dots.forEach((dot, idx) => {
        dot.classList.toggle('hero-carousel__dot--active', idx === this.currentIndex);
        dot.setAttribute('aria-current', idx === this.currentIndex ? 'true' : 'false');
      });
    }

    // Update slide visibility
    this.slides.forEach((slide, idx) => {
      slide.setAttribute('aria-hidden', idx === this.currentIndex ? 'false' : 'true');
    });
  }

  nextSlide() {
    if (!this.slides || this.slides.length === 0) return;
    // @ts-ignore
    this.goToSlide(this.currentIndex + 1);
  }

  prevSlide() {
    if (!this.slides || this.slides.length === 0) return;
    // @ts-ignore
    this.goToSlide(this.currentIndex - 1);
  }

  handlePrevClick() {
    this.prevSlide();
    this.pauseAutoPlay();
    setTimeout(() => this.resumeAutoPlay(), 500);
  }

  handleNextClick() {
    this.nextSlide();
    this.pauseAutoPlay();
    setTimeout(() => this.resumeAutoPlay(), 500);
  }

  /**
   * @param {Event} e - The click event
   */
  handleDotClick(e) {
    if (!e || !e.target) return;
    const target = e.target;
    // @ts-ignore
    const indexStr = target.getAttribute('data-dot-index');
    if (!indexStr) return;
    const index = parseInt(indexStr, 10);
    if (isNaN(index)) return;
    
    this.goToSlide(index);
    this.pauseAutoPlay();
    setTimeout(() => this.resumeAutoPlay(), 500);
  }

  /**
   * @param {TouchEvent} e - The touch event
   */
  handleTouchStart(e) {
    if (!e || !e.touches || e.touches.length === 0) return;
    // @ts-ignore
    this.touchStartX = e.touches[0].clientX;
  }

  /**
   * @param {TouchEvent} e - The touch event
   */
  handleTouchEnd(e) {
    if (!e || !e.changedTouches || e.changedTouches.length === 0) return;
    // @ts-ignore
    this.touchEndX = e.changedTouches[0].clientX;
    this.handleSwipe();
  }

  handleSwipe() {
    const swipeThreshold = 50;
    if (typeof this.touchStartX !== 'number' || typeof this.touchEndX !== 'number') return;
    
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
      this.pauseAutoPlay();
      setTimeout(() => this.resumeAutoPlay(), 500);
    }
  }

  /**
   * @param {KeyboardEvent} e - The keyboard event
   */
  handleKeydown(e) {
    if (!e) return;
    switch (e.key) {
      case 'ArrowLeft':
        this.prevSlide();
        e.preventDefault();
        break;
      case 'ArrowRight':
        this.nextSlide();
        e.preventDefault();
        break;
      case ' ':
        if (this.isAutoPlaying) {
          this.pauseAutoPlay();
        } else {
          this.resumeAutoPlay();
        }
        e.preventDefault();
        break;
    }
  }

  startAutoPlay() {
    if (this.autoPlay && !this.isAutoPlaying) {
      if (typeof this.autoPlaySpeed === 'number' && this.autoPlaySpeed > 0) {
        this.autoPlayInterval = setInterval(() => this.nextSlide(), this.autoPlaySpeed);
        this.isAutoPlaying = true;
      }
    }
  }

  pauseAutoPlay() {
    if (this.autoPlayInterval !== null && typeof this.autoPlayInterval === 'number') {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
    this.isAutoPlaying = false;
  }

  resumeAutoPlay() {
    if (this.autoPlay) {
      this.startAutoPlay();
    }
  }

  destroy() {
    this.pauseAutoPlay();
  }
}

// Initialize all carousels on page
function initCarousels() {
  try {
    const carousels = document.querySelectorAll('[data-section-id]');
    if (!carousels) return;

    carousels.forEach((carousel) => {
      if (!carousel) return;
      const sectionId = carousel.getAttribute('data-section-id');
      if (!sectionId) return;
      
      const hasHeroCarouselClass = carousel.classList && carousel.classList.contains('hero-carousel');
      if (hasHeroCarouselClass) {
        new EnhancedHeroCarousel(sectionId);
      }
    });
  } catch (error) {
    console.warn('Error initializing carousels:', error);
  }
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCarousels);
} else {
  initCarousels();
}

// Re-initialize on Shopify theme editor updates
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    // @ts-ignore
    if (!e || !e.detail) return;
    try {
      // @ts-ignore
      const sectionId = (e).detail.sectionId;
      if (!sectionId) return;
      
      const section = document.querySelector(`[data-section-id="${sectionId}"]`);
      if (!section) return;
      
      const hasHeroCarouselClass = section.classList && section.classList.contains('hero-carousel');
      if (hasHeroCarouselClass) {
        new EnhancedHeroCarousel(sectionId);
      }
    } catch (error) {
      console.warn('Error in section:load handler:', error);
    }
  });
}

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = EnhancedHeroCarousel;
}
